<template>
  <div class="grid xl:grid-cols-2 grid-cols-1 gap-5 mt-6">
    <div class="xl:col-span-2 flex flex-col gap-2">
      <Alert
          v-if="verificationState === 'Approved'"
          type="success-light"
          dismissible
          icon="akar-icons:double-check"
      >
        {{ $t('message.your identity is approved') }}
      </Alert>
      <Alert
          v-else-if="verificationState === 'Rejected'"
          type="danger-light"
          dismissible
          icon="heroicons-outline:exclamation"
      >
        {{ $t('message.your identity verification is rejected') }}
      </Alert>
      <Alert
          v-else-if="verificationState === 'UnderReview'"
          type="primary-light"
          dismissible
          icon="heroicons-outline:information-circle"
      >
        {{ $t('message.your identity verification is under review') }}
      </Alert>


      <Alert
          v-else-if="verificationState === 'Unverified'"
          type="warning-light"
          dismissible
          icon="fluent:target-20-regular"
      >
        {{ $t('message.your identity verification is unverified') }}
      </Alert>


      <Card :title="$t('message.identity verification')">
        <!--        <p>{{ userStore.verificationState }}</p>-->
        <form
            @submit.prevent="onSubmit"
            class="lg:grid-cols-2 grid gap-5 grid-cols-1"
        >
          <TextInput
              :label="$t('message.firstName.label')"
              type="text"
              :placeholder="$t('message.firstName.placeholder')"
              name="firstName"
              :isReadonly="loading"
              v-model="firstName"
              :error="firstNameError"
              :classInput="loading ? 'animate-pulse' : ''"
          />
          <TextInput
              :label="$t('message.lastName.label')"
              type="text"
              :placeholder="$t('message.lastName.placeholder')"
              placeholder="Type your last name"
              name="lastName"
              :isReadonly="loading"
              v-model="lastName"
              :error="lastNameError"
              :classInput="loading ? 'animate-pulse' : ''"
          />
          <TextInput
              :label="$t('message.address.label')"
              type="text"
              :placeholder="$t('message.address.placeholder')"
              name="address"
              :isReadonly="loading"
              v-model="address"
              :error="addressError"
              :classInput="loading ? 'animate-pulse' : ''"
          />
          <TextInput
              :label="$t('message.phoneNumber.label')"
              :placeholder="$t('message.phoneNumber.placeholder')"
              name="phoneNumber"
              v-model="phoneNumber"
              :error="phoneNumberError"
              :isReadonly="loading"
              :options="options.phone"
              :classInput="loading ? 'animate-pulse' : ''"
              isMask
          />
          <div v-if="loading">
            <label class="inline-block input-label">{{ $t('message.id_card.label') }}</label>
            <DropZoneVue :desc="$t('message.id_card.placeholder')"
                         :isLoading="loading"/>
          </div>
          <div v-else>
            <label class="inline-block input-label">{{ $t('message.id_card.label') }}</label>
            <DropZoneVue @uploading="uploadingIdCard = $event"
                         @uploaded="getKycIdCardFileName"
                         @uploadFailed="removedKycIdCard"
                         @removed="removedKycIdCard"
                         :desc="$t('message.id_card.placeholder')"
                         :img="formValues.kycIdCard.link"/>
          </div>
          <div v-if="loading">
            <label class="inline-block input-label">{{ $t('message.selfie.label') }}</label>
            <DropZoneVue :desc="$t('message.selfie.placeholder')"
                         :isLoading="loading"/>
          </div>
          <div v-else>
            <label class="inline-block input-label">{{ $t('message.selfie.label') }}</label>
            <DropZoneVue @uploading="uploadingSelfie = $event"
                         @uploaded="getKycSelfieFileName"
                         @uploadFailed="removedKycSelfie"
                         @removed="removedKycSelfie"
                         :desc="$t('message.selfie.placeholder')"
                         :img="formValues.kycSelfie.link"/>
          </div>
          <div class="lg:col-span-2">
            <Button btnClass="btn-dark" :isDisabled="!canSubmitForm()">
              <span class="flex gap-2">
                {{ $t('message.submit') }}
                <Icon v-if="loading" icon="eos-icons:bubble-loading" height="24"/>
              </span>
            </Button>
          </div>
        </form>
      </Card>
    </div>
  </div>
</template>
<script setup>
import Button from "@/components/Button/index.vue";
import TextInput from "@/components/Textinput/index.vue";
import Card from "@/components/Card/index.vue";
import {computed, onMounted, reactive, ref} from "vue";
import {useUserStore} from "@/stores/user.js";
import DropZoneVue from "@/views/profile/dropZone.vue";
import {useField, useForm} from 'vee-validate';
import * as yup from 'yup';
import {useI18n} from 'vue-i18n';
import {useToast} from "vue-toastification";
import {useThemeSettingsStore} from "@/stores/themeSettings.js";
import {Icon} from "@iconify/vue";
import {useUploadStore} from "@/stores/upload.js";
import Alert from '@/components/Alert/index.vue';

const {t, locale} = useI18n({useScope: 'global'})
const userStore = useUserStore();
const uploadStore = useUploadStore();
const themeSettings = useThemeSettingsStore();
const toast = useToast();
const loading = ref(true)
const uploadingIdCard = ref(false)
const uploadingSelfie = ref(false)

const uploading = computed(() =>
    uploadingIdCard.value || uploadingSelfie.value
)
const verificationState = ref();
const formValues = reactive({
  kycSelfie: {},
  kycIdCard: {}
})

const schema = yup.object({
  firstName: yup.string().required(t('validation.not_null {label}', {label: t('message.firstName.label')})).label(t('message.firstName.label')).trim(),
  lastName: yup.string().required(t('validation.not_null {label}', {label: t('message.lastName.label')})).label(t('message.lastName.label')).trim(),
  address: yup.string().required(t('validation.not_null {label}', {label: t('message.address.label')})).label(t('message.address.label')).trim(),
  phoneNumber: yup.string().required(t('validation.not_null {label}', {label: t('message.phoneNumber.label')})).nullable(t('validation.not_null {label}', {label: t('message.phoneNumber.label')})).label(t('message.phoneNumber.label')).trim(),
});

const {handleSubmit} = useForm({
  validationSchema: schema
});

const {value: firstName, errorMessage: firstNameError} = useField("firstName");
const {value: lastName, errorMessage: lastNameError} = useField("lastName");
const {value: address, errorMessage: addressError} = useField("address");
const {value: phoneNumber, errorMessage: phoneNumberError} = useField("phoneNumber");

const options = {
  phone: {
    phone: true,
    phoneRegionCode: 'IR',
    numeral: true,
    delimiter: "",
    onlyPositive: true,
    numeralPositiveOnly: true,
    numericOnly: true,
    stripLeadingZeroes: false,
  }
}
const uploadedSelfie = ref(false);
const uploadedIdCard = ref(false);

const onSubmit = handleSubmit(async () => {
  loading.value = true;
  const data = {
    firstName: firstName.value,
    lastName: lastName.value,
    address: address.value,
    phoneNumber: phoneNumber.value,
  }
  if (canSubmitForm()) {
    const submitted = await userStore.saveIdentificationDocuments({...data, ...formValues});
    if (submitted) {
      toast.success(t("message.submitted successfully"), {
        timeout: 2500,
        rtl: themeSettings.direction,
        toastClassName: "!mr-0 !ml-auto",
      });
      await getProfile();
    } else {
      toast.error(message.value, {
        timeout: 3000,
      });
    }
  }
  loading.value = false;
})

const message = computed(() => {
  return userStore.getErrorMessage;
});

async function getProfile() {
  await userStore.action_getCompleteProfile3()
  verificationState.value = userStore.getProfile.verificationState
  if (verificationState.value !== "Unverified") {
    firstName.value = userStore.getProfile.firstName
    lastName.value = userStore.getProfile.lastName
    address.value = userStore.getProfile.address
    phoneNumber.value = (userStore.getProfile.phoneNumber).toString()
    formValues.kycSelfie = userStore.getProfile.kycSelfie
    formValues.kycIdCard = userStore.getProfile.kycIdCard
    uploadedIdCard.value = true
    uploadedSelfie.value = true
  }
  loading.value = false
}

function canSubmitForm() {
  return (!uploading.value && uploadedIdCard.value && uploadedSelfie.value)
}

function removedKycSelfie() {
  formValues.kycSelfie = {}
  uploadedSelfie.value = false
}

function removedKycIdCard() {
  formValues.kycIdCard = {}
  uploadedIdCard.value = false
}

function getKycIdCardFileName(fileName) {
  formValues.kycIdCard = {
    name: fileName,
    action: "replace"
  }
  uploadedIdCard.value = true
}

function getKycSelfieFileName(fileName) {
  formValues.kycSelfie = {
    name: fileName,
    action: "replace"
  }
  uploadedSelfie.value = true
}

onMounted(() => {
  getProfile();
})
</script>